import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Grid, Typography, alpha, Divider } from "@mui/material";
import downloadWord from "../../Assets/Wolfsberg-Lite-Due-Diligence-Questionnaire.docx";
import downloadPdf from "../../Assets/New-Authorized-Representative-Template-v1.1.pdf";
import BeneficialOwners from "./BeneficialOwners";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

const AuthorizationLatterUpload = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
  steps,
  payload,
  getUserDetails,
}) => {
  const { btncolor } = useSelector((state) => state);
  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");
  const [payloadSecond, setPayloadSecond] = useState({ ...payload });
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    trigger,
    watch,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: userDetails?.enterpriseUser,
  });

  const fileValidation = (required = false) => ({
    validate: (fileList) => {
      if (!fileList || fileList.length === 0) {
        return required ? "This file is required." : true;
      }

      const file = fileList[0];
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jfif",
        "image/heic",
      ];

      if (!validTypes.includes(file.type)) {
        return "Invalid file type";
      }

      if (file.size > 6 * 1024 * 1024) {
        return "MAX limit 6 MB.";
      }
    },
  });

  const onSubmit = async (data, action) => {
    const updatedPayload = {
      wolfsbergDoc: data?.wolfsbergDoc
        ? data?.wolfsbergDoc[0]
        : data?.authorizationLetter[0],
      authorizationLetter: data?.authorizationLetter
        ? data?.authorizationLetter[0]
        : data?.wolfsbergDoc[0],
    };
    setPayloadSecond((prev) => ({ ...prev, ...updatedPayload }));
    handleSteps();
    reset();
  };

  return (
    <>
      {steps === 4 && (
        <Box sx={card}>
          {/* Step Header */}
          <Box sx={stepHeader}>
            <Box
              sx={{
                ...stepBadge,
                backgroundColor: alpha(btncolor || "#1976d2", 0.12),
                color: btncolor,
              }}
            >
              <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={stepTitle}>Authorization Documents</Typography>
              <Typography sx={stepCaption}>
                Upload Wolfsberg Questionnaire and Authorization Letter
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Wolfsberg Questionnaire */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={fieldLabel}>Wolfsberg Questionnaire</Typography>
                <Typography sx={descText}>
                  Download the Wolfsberg Questionnaire template{" "}
                  <a
                    target="_blank"
                    href={downloadWord}
                    style={{ color: "inherit", fontWeight: 600 }}
                  >
                    here
                  </a>
                  . Fill it and upload it. This is mandatory for all financial
                  companies.
                </Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon
                      sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }}
                    />
                    <Typography sx={uploadHintInner}>
                      Click to select file
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => {
                      const fileList = e.target.files;
                      setValue("wolfsbergDoc", fileList);
                      trigger("wolfsbergDoc");
                    }}
                    {...register("wolfsbergDoc", fileValidation(false))}
                  />
                </Box>
                {errors.wolfsbergDoc && (
                  <Typography sx={errorText}>
                    {errors.wolfsbergDoc.message}
                  </Typography>
                )}
                <Typography sx={hintText}>
                  Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC —
                  MAX 6 MB
                </Typography>
              </Grid>

              {/* Authorization Letter */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={fieldLabel}>Authorization Letter *</Typography>
                <Typography sx={descText}>
                  If there are persons authorized to access and operate the
                  account, upload an Authorization Letter. Download the template{" "}
                  <a
                    href={downloadPdf}
                    download
                    style={{ color: "inherit", fontWeight: 600 }}
                  >
                    here
                  </a>
                  . Fill it out, sign it, and upload the signed copy.
                </Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon
                      sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }}
                    />
                    <Typography sx={uploadHintInner}>
                      Click to select file
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => {
                      const fileList = e.target.files;
                      setValue("authorizationLetter", fileList);
                      trigger("authorizationLetter");
                    }}
                    {...register("authorizationLetter", fileValidation(true))}
                  />
                </Box>
                {errors.authorizationLetter && (
                  <Typography sx={errorText}>
                    {errors.authorizationLetter.message}
                  </Typography>
                )}
                <Typography sx={hintText}>
                  Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC —
                  MAX 6 MB
                </Typography>
              </Grid>
            </Grid>

            <Box sx={actionRow}>
              <ContainedButton
                variant="contained"
                onClick={handleBack}
                sx={{ ...submitBtn, opacity: 0.85 }}
              >
                Back
              </ContainedButton>
              <ContainedButton
                type="submit"
                variant="contained"
                sx={submitBtn}
                disabled={!watch("authorizationLetter")?.length}
                onClick={handleSubmit((data) => onSubmit(data, "next"))}
              >
                Next
              </ContainedButton>
            </Box>
          </form>
        </Box>
      )}

      {steps > 4 && (
        <BeneficialOwners
          handleSteps={handleSteps}
          handleBack={handleBack}
          userDetails={userDetails}
          setIsloading={setIsloading}
          steps={steps}
          payload={payloadSecond}
        />
      )}
    </>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.5,
};

const descText = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.6,
  lineHeight: 1.6,
  mb: 1,
};

const uploadCard = {
  border: "2px dashed rgba(125,125,125,0.3)",
  borderRadius: "10px",
  p: 1.5,
  mt: 0.5,
  backgroundColor: "var(--body-color-less2)",
};

const uploadIconRow = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mb: 0.75,
};

const uploadHintInner = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.5,
};

const fileInputStyle = {
  width: "100%",
  color: "var(--text-color)",
  fontSize: "0.85rem",
};

const hintText = {
  fontSize: "0.7rem",
  color: "var(--text-color)",
  opacity: 0.4,
  mt: 0.75,
  lineHeight: 1.5,
};

const errorText = {
  fontSize: "0.72rem",
  color: "#ef4444",
  mt: 0.5,
};

const actionRow = {
  mt: 3.5,
  pt: 2.5,
  borderTop: "1px solid rgba(125,125,125,0.12)",
  display: "flex",
  gap: 1.5,
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default AuthorizationLatterUpload;
